import React, { createContext, useContext, useState, type ReactNode, useCallback } from 'react';
import Confirm, { type ConfirmStyle } from '../components/base/Confirm';

interface ConfirmContextProps {
    showConfirm: (options: ConfirmOptions) => Promise<boolean>;
}

interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmButtonColor?: 'primary' | 'danger' | 'warning';
    style?: ConfirmStyle; // 'modal' | 'actionSheet'
}

interface ConfirmState {
    isOpen: boolean;
    title?: string;
    message: string;
    confirmText: string;
    cancelText: string;
    confirmButtonColor: 'primary' | 'danger' | 'warning';
    style: ConfirmStyle;
    onConfirm: () => void;
    onCancel: () => void;
}

const initialConfirmState: ConfirmState = {
    isOpen: false,
    message: '',
    confirmText: '확인',
    cancelText: '취소',
    confirmButtonColor: 'primary',
    style: 'modal',
    onConfirm: () => { },
    onCancel: () => { },
};

const ConfirmContext = createContext<ConfirmContextProps | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [confirmState, setConfirmState] = useState<ConfirmState>(initialConfirmState);

    const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                title: options.title,
                message: options.message,
                confirmText: options.confirmText ?? '확인',
                cancelText: options.cancelText ?? '취소',
                confirmButtonColor: options.confirmButtonColor ?? 'primary',
                style: options.style ?? 'modal',
                onConfirm: () => {
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                    resolve(false);
                },
            });
        });
    }, []);

    return (
        <ConfirmContext.Provider value={{ showConfirm }}>
            {children}
            <Confirm
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                message={confirmState.message}
                confirmText={confirmState.confirmText}
                cancelText={confirmState.cancelText}
                confirmButtonColor={confirmState.confirmButtonColor}
                style={confirmState.style}
                onConfirm={confirmState.onConfirm}
                onCancel={confirmState.onCancel}
            />
        </ConfirmContext.Provider>
    );
};

export const useConfirm = (): ConfirmContextProps => {
    const context = useContext(ConfirmContext);
    if (context === undefined) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context;
};